"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
Object.defineProperty(exports, "__esModule", { value: true });
var react_1 = require("react");
var validator_1 = require("@aperza/validator");
var createFieldMap = function (config, data) {
    if (data === void 0) { data = {}; }
    var fieldMap = {};
    Object.keys(config).forEach(function (key) {
        var _a, _b;
        var configEntry = config[key];
        var value = (_b = (_a = data === null || data === void 0 ? void 0 : data[key]) !== null && _a !== void 0 ? _a : configEntry.value) !== null && _b !== void 0 ? _b : '';
        if (data && !(key in data) && configEntry.value) {
            data[key] = configEntry.value;
        }
        fieldMap[key] = __assign(__assign({}, configEntry), { value: value, errorId: '', constraint: configEntry.constraint, options: configEntry.options });
    });
    return fieldMap;
};
var updateFieldMapData = function (fieldMap, data) {
    var clonedFieldMap = Object.assign({}, fieldMap);
    if (clonedFieldMap && data) {
        Object.keys(clonedFieldMap).map(function (key) {
            var _a;
            clonedFieldMap[key].value = (_a = data[key]) !== null && _a !== void 0 ? _a : '';
        });
    }
    return clonedFieldMap;
};
var clearFieldMapError = function (fieldMap) {
    var clonedFieldMap = Object.assign({}, fieldMap);
    if (clonedFieldMap) {
        Object.keys(clonedFieldMap).map(function (key) {
            clonedFieldMap[key].errorId = '';
            clonedFieldMap[key].invalid = false;
        });
    }
    return clonedFieldMap;
};
var updateFieldMapValue = function (fieldMap, field, value) {
    var clonedFieldMap = Object.assign({}, fieldMap);
    var shouldUpdate = (clonedFieldMap[field].floatOnly && !isNaN(parseFloat(value)) && /^-?\d*\.?\d*$/.test(value)) ||
        (clonedFieldMap[field].intOnly && !isNaN(parseInt(value)) && /^-?\d*$/.test(value)) ||
        (!clonedFieldMap[field].floatOnly &&
            !clonedFieldMap[field].intOnly &&
            clonedFieldMap[field].value !== value) ||
        value === '';
    if (shouldUpdate) {
        clonedFieldMap[field].value = value;
    }
    return clonedFieldMap;
};
var updateFieldMapErrorId = function (fieldMap, field, errorId) {
    var clonedFieldMap = Object.assign({}, fieldMap);
    clonedFieldMap[field].errorId = errorId;
    clonedFieldMap[field].invalid = !!errorId;
    return clonedFieldMap;
};
var updateFieldMapConstraint = function (fieldMap, field, constraint) {
    var clonedFieldMap = Object.assign({}, fieldMap);
    clonedFieldMap[field].constraint = constraint;
    return clonedFieldMap;
};
/**
 * A hook to manage the fields of a form.
 * A config is an object to hold the configuration of each field.
 * Example of a config entry.
 * first_name: {
      fieldName: 'first_name',
      constraint: Constraint.firstName,
      value: user.firstName ? user.firstName : '',
   },
 *
 * fieldName: property name of the field
 * constraint: field validation rules using validate.js refer to @aperza/validation project
 * value: initial value. Must not be undefined otherwise the Controlled React Component will become Uncontrolled Component.
 * errorId: Error message from validator
 * invalid: a flag to indicate whether there is a validation error in each field.
 *
 * @param config
 * @param initialData
 */
exports.useFieldMap = function (_config, _initialData) {
    var _a = react_1.useState(_config), config = _a[0], setConfig = _a[1];
    var _b = react_1.useState(_initialData), initialData = _b[0], setInitialData = _b[1];
    var _c = react_1.useState(createFieldMap(_config, _initialData)), fieldMap = _c[0], setFieldMap = _c[1];
    var updateFieldMapConfig = function (initialConfig, _initialData) {
        setConfig(initialConfig);
        setInitialData(_initialData);
        setFieldMap(createFieldMap(initialConfig, _initialData));
    };
    var updateFieldMap = function (data) {
        if (data === void 0) { data = {}; }
        setFieldMap(updateFieldMapData(fieldMap, data));
    };
    var resetFieldMap = function (data) {
        if (data === void 0) { data = initialData; }
        updateFieldMap(data);
        setFieldMap(clearFieldMapError(fieldMap));
    };
    var clearFieldMapErrorIds = function () {
        setFieldMap(clearFieldMapError(fieldMap));
    };
    var updateFieldValue = function (field, value) {
        if (value === void 0) { value = ''; }
        if (field) {
            setFieldMap(updateFieldMapValue(fieldMap, field, value));
        }
    };
    var updateFieldErrorId = function (field, errorId) {
        if (errorId === void 0) { errorId = ''; }
        if (field) {
            setFieldMap(updateFieldMapErrorId(fieldMap, field, errorId));
        }
    };
    var updateFieldConstraint = function (field, constraint) {
        if (field) {
            setFieldMap(updateFieldMapConstraint(fieldMap, field, constraint));
        }
    };
    var validateFieldMap = function (returnErrorCount) {
        var validatedFieldMap = validator_1.ValidatorUtil.validateForm(fieldMap);
        setFieldMap(validatedFieldMap);
        if (returnErrorCount)
            return validator_1.ValidatorUtil.countError(validatedFieldMap);
        return validatedFieldMap;
    };
    var validateField = function (field) {
        var validatedFieldMap = validator_1.ValidatorUtil.validateFormField(fieldMap, field);
        setFieldMap(validatedFieldMap);
        return validator_1.ValidatorUtil.countError(validatedFieldMap);
    };
    var getFieldMapValue = function () {
        var clonedFieldMap = Object.assign({}, fieldMap);
        var value = {};
        if (clonedFieldMap) {
            Object.keys(clonedFieldMap).map(function (key) {
                value[key] = clonedFieldMap[key].value;
            });
        }
        return value;
    };
    var getFieldValue = function (field) {
        var clonedFieldMap = Object.assign({}, fieldMap);
        if (clonedFieldMap) {
            return clonedFieldMap[field].value;
        }
        return '';
    };
    return {
        fieldMap: fieldMap,
        setFieldMap: setFieldMap,
        updateFieldMapConfig: updateFieldMapConfig,
        updateFieldMap: updateFieldMap,
        resetFieldMap: resetFieldMap,
        clearFieldMapErrorIds: clearFieldMapErrorIds,
        updateFieldValue: updateFieldValue,
        updateFieldConstraint: updateFieldConstraint,
        updateFieldErrorId: updateFieldErrorId,
        validateFieldMap: validateFieldMap,
        validateField: validateField,
        getFieldMapValue: getFieldMapValue,
        getFieldValue: getFieldValue,
    };
};
exports.default = exports.useFieldMap;
//# sourceMappingURL=UseFieldMap.js.map